import JSZip from 'jszip';
import { Project, Station, ProjectConfig, Route, CalculationResult } from '../../types';
import { getEffectivePopulation } from '../../utils/population';
import { CURRENCIES } from '../../config/currencies';
import { generateExcelFile } from './excel';
import cytoscape from 'cytoscape';
import { generateMatrixContent } from './utils';
import { getEdgeLabel } from '../../utils/distance';
import { MATRIX_DEFINITIONS } from '../url/matrixMapping';
import { formatCurrencyNoDecimals } from '../../lib/format/currency';
import { MatrixType } from '../url/matrixMapping';
import ExcelJS from 'exceljs';
import { getEffectiveDistance } from '../../utils/distance';
import { getSortedStationIndices, getSortedStations, reorderMatrix } from '../matrix/ordering';

export function generateConfigJSON(config: ProjectConfig): string {
  const orderedConfig = {
    // Project Name (not included in config, handled separately)
    
    // Construction Cost (Bond settings)
    bond: {
      constructionCostPerKm: config.costs.constructionCostPerKm,
      duration: config.bond.duration,
      interestRate: Number((config.bond.interestRate / 100).toFixed(4)),
      currency: config.bond.currency
    },
    // Ticket Price (Costs)
    costs: {
      farePerKm: config.costs.farePerKm,
      operatingCostPerKm: config.costs.operatingCostPerKm,
      businessShareOfSeats: Number((config.costs.businessShareOfSeats / 100).toFixed(4)),
      businessTicketPremium: Number((config.costs.businessTicketPremium / 100).toFixed(4)),
      discountShareOfSeats: Number((config.costs.discountShareOfSeats / 100).toFixed(4)),
      discountTicketReduction: Number((config.costs.discountTicketReduction / 100).toFixed(4))
    },
    // Train Settings
    trains: {
      seatsPerTrain: config.trains.seatsPerTrain,
      loadFactor: Number((config.trains.loadFactor / 100).toFixed(4)),
      maxSpeed: config.trains.maxSpeed,
      minimumStationDwellTime: config.trains.minimumStationDwellTime,
      maximumStationDwellTime: config.trains.maximumStationDwellTime,
      minimumHeadway: config.trains.minimumHeadway
    },
    // Calculation Settings
    calculation: {
      calibrationConstant: config.calculation.calibrationConstant,
      calibrationConstantCustoms: config.calculation.calibrationConstantCustoms,
      calibrationConstantNoCustoms: config.calculation.calibrationConstantNoCustoms,
      densityFrictionFactor: config.calculation.densityFrictionFactor,
      distanceFrictionFactor: config.calculation.distanceFrictionFactor,
      minimumRouteDistance: config.calculation.minimumRouteDistance,
      populationRadius: config.calculation.populationRadius
    }
  };
  return JSON.stringify(orderedConfig, null, 2);
}

export async function exportNetworkImage(cy: cytoscape.Core): Promise<Blob> {
  // Get the project data from the original Cytoscape instance
  const project = cy.container() ? cy.data('project') : cy.scratch('project');
  if (!project) {
    console.error('Project data not found in Cytoscape instance');
    throw new Error('Project data not found in Cytoscape instance');
  }

  // Create a new Cytoscape instance for image export
  const exportCy = cytoscape({
    container: document.createElement('div'),
    data: { project }, // Store project data in the export instance
    elements: {
      nodes: cy.nodes().map(node => ({
        data: node.data(),
        position: node.position()
      })),
      edges: cy.edges().map(edge => ({
        data: {
          id: edge.data('id'),
          source: edge.source().id(),
          target: edge.target().id(),
          route: edge.data('route'),
          label: edge.data('label'),
          passengers: edge.data('passengers') || 0,
          passengersPerKm: edge.data('passengersPerKm') || 0
        }
      }))
    },
    style: [
      {
        selector: 'node',
        style: {
          'background-color': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            const population = station ? getEffectivePopulation(station) : 0;
            return population === 0 ? '#666666' : '#6B89B0';
          },
          'border-width': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            return station?.latitude && station?.longitude ? 0 : 1;
          },
          'border-color': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            return station?.latitude && station?.longitude ? '#666666' : '#000000';
          },
          'border-opacity': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            return station?.latitude && station?.longitude ? 0 : 1;
          },
          'label': 'data(name)',
          'width': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            const population = station ? getEffectivePopulation(station) : 0;
            const baseSize = 20;
            
            // Get all stations' populations, excluding zeros
            const allNodes = ele.cy().nodes();
            const populations = allNodes
              .map(node => {
                const station = node.data('station');
                return station ? getEffectivePopulation(station) : 0;
              })
              .filter(pop => pop > 0);
            
            // If no non-zero populations, return base size
            if (populations.length === 0) return `${baseSize}px`;
            
            const minPop = Math.min(...populations);
            const maxPop = Math.max(...populations);
            
            // If all non-zero populations are the same, return base size
            if (maxPop === minPop) return `${baseSize}px`;
            
            // Use logarithmic scaling for smoother transitions
            const logMin = Math.log(minPop + 1);
            const logMax = Math.log(maxPop + 1);
            const logPop = Math.log(population + 1);
            
            // Scale between 1x and 3x base size using log scale, clamped to prevent negative values
            const scale = Math.max(1, Math.min(3, 1 + ((logPop - logMin) / (logMax - logMin)) * 2));
            return `${baseSize * scale}px`;
          },
          'height': (ele: cytoscape.NodeSingular) => {
            const station = ele.data('station');
            const population = station ? getEffectivePopulation(station) : 0;
            const baseSize = 20;
            
            // Get all stations' populations, excluding zeros
            const allNodes = ele.cy().nodes();
            const populations = allNodes
              .map(node => {
                const station = node.data('station');
                return station ? getEffectivePopulation(station) : 0;
              })
              .filter(pop => pop > 0);
            
            // If no non-zero populations, return base size
            if (populations.length === 0) return `${baseSize}px`;
            
            const minPop = Math.min(...populations);
            const maxPop = Math.max(...populations);
            
            // If all non-zero populations are the same, return base size
            if (maxPop === minPop) return `${baseSize}px`;
            
            // Use logarithmic scaling for smoother transitions
            const logMin = Math.log(minPop + 1);
            const logMax = Math.log(maxPop + 1);
            const logPop = Math.log(population + 1);
            
            // Scale between 1x and 3x base size using log scale, clamped to prevent negative values
            const scale = Math.max(1, Math.min(3, 1 + ((logPop - logMin) / (logMax - logMin)) * 2));
            return `${baseSize * scale}px`;
          },
          'font-size': '12px',
          'font-weight': 'bold',
          'text-valign': 'top',
          'text-halign': 'center',
          'text-wrap': 'wrap',
          'text-max-width': '80px',
          'color': '#333333',
          'background-opacity': 1,
          'text-background-color': '#ffffff',
          'text-background-opacity': 1,
          'text-background-padding': '6px',
          'text-background-shape': 'roundrectangle',
          'text-border-width': 1,
          'text-border-color': '#cccccc',
          'text-border-opacity': 1,
          'text-margin-y': -15
        }
      },
      {
        selector: 'edge',
        style: {
          'width': (ele: cytoscape.EdgeSingular) => {
            const passengers = ele.data('passengers');
            const route = ele.data('route');
            const baseWidth = 3;
            const maxWidth = 10;
            
            // Get project from either data or scratch space
            const cy = ele.cy();
            const project = cy.container() ? cy.data('project') : cy.scratch('project');
            if (!project) {
              console.warn('Project data not found for edge width calculation');
              return baseWidth;
            }
            
            // If route has 0 distance or passengers is invalid, return base width
            if (getEffectiveDistance(route, project) === 0 || typeof passengers !== 'number' || isNaN(passengers)) {
              return baseWidth;
            }
            
            // Get all edges' passenger counts, filtering out invalid values
            const allEdges = ele.cy().edges();
            const passengerCounts = allEdges
              .map((edge: cytoscape.EdgeSingular) => {
                const count = edge.data('passengers');
                return typeof count === 'number' && !isNaN(count) ? count : 0;
              })
              .filter((count: number) => count > 0);
            
            // If no valid passenger counts, return base width
            if (passengerCounts.length === 0) return baseWidth;
            
            const minPassengers = Math.min(...passengerCounts);
            const maxPassengers = Math.max(...passengerCounts);
            
            // If all passenger counts are the same, return base width
            if (maxPassengers === minPassengers) return baseWidth;
            
            // Use linear scaling and ensure we get a positive value
            const scale = Math.max(0, Math.min(1, (passengers - minPassengers) / (maxPassengers - minPassengers)));
            return baseWidth + (maxWidth - baseWidth) * scale;
          },
          'line-color': '#666',
          'line-style': (ele) => {
            const route = ele.data('route') as Route;
            switch (route.landType) {
              case 'customs': return 'dashed';
              case 'no-customs': return 'dotted';
              default: return 'solid';
            }
          },
          'curve-style': 'straight',
          'label': 'data(label)',
          'text-rotation': 'autorotate',
          'text-margin-y': 0,
          'font-size': '10px',
          'text-valign': 'center',
          'text-halign': 'center',
          'text-wrap': 'wrap',
          'text-max-width': '80px',
          'color': '#333333',
          'text-background-color': '#ffffff',
          'text-background-opacity': 1,
          'text-background-padding': '4px',
          'text-background-shape': 'roundrectangle',
          'text-border-width': 1,
          'text-border-color': '#cccccc',
          'text-border-opacity': 1
        }
      }
    ],
    layout: {
      name: 'preset',
      positions: Object.fromEntries(
        cy.nodes().map(node => [
          node.id(),
          { x: node.position('x'), y: node.position('y') }
        ])
      ),
      padding: 50
    }
  });

  // Also store project in scratch space as backup
  exportCy.scratch('project', project);

  // Calculate the network's bounding box
  const boundingBox = exportCy.elements().boundingBox({
    includeLabels: true,
    includeOverlays: true,
    includeEdges: true
  });

  // Calculate dimensions and aspect ratio
  const width = boundingBox.w;
  const height = boundingBox.h;
  const aspectRatio = width / height;

  // Define maximum dimensions for Excel
  const maxWidth = 1000;
  const maxHeight = 750;

  // Calculate new dimensions maintaining aspect ratio
  let finalWidth, finalHeight;
  if (aspectRatio > maxWidth / maxHeight) {
    // Width is the limiting factor
    finalWidth = maxWidth;
    finalHeight = maxWidth / aspectRatio;
  } else {
    // Height is the limiting factor
    finalHeight = maxHeight;
    finalWidth = maxHeight * aspectRatio;
  }

  // Export graph as PNG with calculated dimensions
  const pngBlob = await exportCy.png({
    output: 'blob',
    bg: '#ffffff',
    maxWidth: Math.round(finalWidth),
    maxHeight: Math.round(finalHeight),
    full: true,
    scale: 2 // High DPI setting
  });

  // Clean up the temporary Cytoscape instance
  exportCy.destroy();

  return pngBlob;
}

function addData(element: Element, key: string, value: string): void {
  const data = document.createElement('data');
  data.setAttribute('key', key);
  data.textContent = value;
  element.appendChild(data);
}

export function cleanProjectName(name: string): string {
  // Remove special characters and replace with underscores
  return name.replace(/[^a-zA-Z0-9-]/g, '_')
    // Replace multiple consecutive underscores with a single one
    .replace(/_+/g, '_')
    // Remove leading/trailing underscores
    .trim().replace(/^_+|_+$/g, '');
}

export async function exportProject(
  project: Project,
  matrices: CalculationResult,
  cy?: cytoscape.Core
): Promise<Blob> {
  const zip = new JSZip();
  const timestamp = formatTimestamp(new Date());
  const cleanedProjectName = cleanProjectName(project.name);

  // Add matrices as CSV files using MATRIX_DEFINITIONS
  const csvFiles = Object.entries(MATRIX_DEFINITIONS)
    .filter(([_, definition]) => definition.allowInResults)
    .map(([key, definition]) => {
      const matrixType = key as MatrixType;
      const baseFilename = definition.view;
      const filename = definition.hasCurrency
        ? `${baseFilename}_${project.config.bond.currency}.csv`
        : `${baseFilename}.csv`;
      
      return {
        name: filename,
        type: matrixType,
        data: matrices[matrixType]
      };
    });

  // Generate CSV content for each file using the centralized generateMatrixContent function
  for (const file of csvFiles) {
    const content = generateMatrixContent(file.type as MatrixType, file.data, project.network.stations, project.config);
    zip.file(file.name, content);
  }

  // Add network as GraphML
  zip.file(
    'network.graphml',
    generateGraphML(project)
  );

  // Add network image if Cytoscape is available
  if (cy) {
    try {
      const networkImage = await exportNetworkImage(cy);
      zip.file('network.png', networkImage);
    } catch (error) {
      console.error('Failed to generate network image:', error);
    }
  }

  // Add Excel file if Cytoscape is available
  if (cy) {
    const excelBuffer = await generateExcelFile({ project, matrices, cy });
    zip.file(
      `${cleanedProjectName}.xlsx`,
      excelBuffer
    );
  }

  return zip.generateAsync({ type: 'blob' });
}

export function formatTimestamp(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}_${hours}${minutes}`;
}

export function generateGraphML(project: Project): string {
  const { stations, routes } = project.network;

  return `<?xml version="1.0" encoding="UTF-8"?>
<graphml xmlns="http://graphml.graphdrawing.org/xmlns">
  <key id="name" for="node" attr.name="name" attr.type="string"/>
  <key id="population" for="node" attr.name="population" attr.type="int"/>
  <key id="population_source" for="node" attr.name="population_source" attr.type="string"/>
  <key id="circle_population" for="node" attr.name="circle_population" attr.type="int"/>
  <key id="circle_population_radius" for="node" attr.name="circle_population_radius" attr.type="double"/>
  <key id="latitude" for="node" attr.name="latitude" attr.type="double"/>
  <key id="longitude" for="node" attr.name="longitude" attr.type="double"/>
  <key id="positionX" for="node" attr.name="positionX" attr.type="double"/>
  <key id="positionY" for="node" attr.name="positionY" attr.type="double"/>
  <key id="distance" for="edge" attr.name="distance" attr.type="double"/>
  <key id="automaticDistance" for="edge" attr.name="automaticDistance" attr.type="double"/>
  <key id="distanceCalculation" for="edge" attr.name="distanceCalculation" attr.type="string"/>
  <key id="passengers" for="edge" attr.name="passengers" attr.type="int"/>
  <key id="border" for="edge" attr.name="border" attr.type="string"/>
  <key id="config" for="graph" attr.name="configuration" attr.type="string"/>
  <key id="projectName" for="graph" attr.name="projectName" attr.type="string"/>
  <key id="identifier" for="graph" attr.name="identifier" attr.type="string"/>
  <graph id="G" edgedefault="undirected">
    <data key="projectName">${project.name}</data>
    ${project.identifier ? `<data key="identifier">${project.identifier}</data>` : ''}
    <data key="config">${generateConfigJSON(project.config)}</data>
    
    ${stations.map(station => `
    <node id="${station.id}">
      <data key="name">${station.name}</data>
      <data key="population">${station.population}</data>
      <data key="population_source">${station.population_source || 'manual'}</data>
      <data key="circle_population">${station.circle_population || ''}</data>
      <data key="circle_population_radius">${station.circle_population_radius || ''}</data>
      <data key="latitude">${station.latitude || ''}</data>
      <data key="longitude">${station.longitude || ''}</data>
      <data key="positionX">${station.position.x}</data>
      <data key="positionY">${station.position.y}</data>
    </node>`).join('')}
    ${routes.map(route => `
    <edge id="${route.id}" source="${route.source}" target="${route.target}">
      <data key="distance">${route.distance}</data>
      <data key="automaticDistance">${route.automaticDistance || ''}</data>
      <data key="distanceCalculation">${route.distanceCalculation}</data>
      <data key="passengers">${project.matrices?.passengersBySegment?.[stations.findIndex(s => s.id === route.source)]?.[stations.findIndex(s => s.id === route.target)] || 0}</data>
      <data key="border">${route.landType || 'domestic'}</data>
    </edge>`).join('')}
  </graph>
</graphml>`;
}

export function downloadMatrix(
  type: MatrixType,
  data: number[][] | Station[],
  project: Project
): void {
  const timestamp = formatTimestamp(new Date());
  const view = MATRIX_DEFINITIONS[type].view;
  const needsCurrency = MATRIX_DEFINITIONS[type].hasCurrency;
  const cleanedProjectName = cleanProjectName(project.name);
  
  // Construct filename based on whether it needs currency
  const filename = needsCurrency
    ? `${cleanedProjectName}_${view}_${project.config.bond.currency}_${timestamp}.csv`
    : `${cleanedProjectName}_${view}_${timestamp}.csv`;

  const content = generateMatrixContent(type, data, project.network.stations, project.config);
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}